import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { CartNav } from "@/features/cart/components/CartNav";
import { useIsMobile } from "@/features/viewer/hooks/useIsMobile";
import { stageIntentPreloadProps } from "@/features/viewer/preloadStageHost";
import { CHROME_SURFACE, stitchClass } from "@/lib/chrome";
import { cn } from "@/lib/utils";

// The app's floating navigation (#ui-polish): ONE connected bar in the warm
// paper-label chrome language — logo + a "stitch" divider (the sewn-pillow motif) +
// the links, so it reads as a single crafted tag. Used on EVERY page (landing, the
// full-screen Stage, the account/editor shells) so the menu + logo are identical
// everywhere; `fixed` so it floats over the fixed Stage canvas AND stays put while
// the scrolling pages scroll under it.
//
// Mobile-first (DESIGN.md §5 "nav collapses to a sheet rather than cramming a desktop
// bar"): on a phone the links fold behind a menu button that opens a sheet — the same
// Sheet vocabulary as the controls bar — so the top strip stays a clean logo + menu.

type NavLink = { to: string; label: string; active?: "exact" };

function useNavLinks(): NavLink[] {
  const { t } = useTranslation();
  // Production menu (no dev-only Stage Lab — it's reachable by URL in dev).
  return [
    { to: "/", label: t("nav.home"), active: "exact" },
    { to: "/upload", label: t("nav.upload") },
    // "/me" account link hidden until account functionality exists (Leon).
  ];
}

// The nav's "Wgraj zdjęcie" entry — a plain link to the fresh /upload front door that warms
// the code-split Stage chunk on pointer/focus intent (#346), so the crossing lands on the
// real studio, not the loading fallback. (The resume-aware furthest-step routing is retired
// with the browser-local Resume pointer, ADR-0056.)
function UploadNavLink({
  label,
  className,
  activeClassName,
  onClick,
}: {
  label: string;
  className: string;
  activeClassName: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to="/upload"
      {...stageIntentPreloadProps}
      onClick={onClick}
      className={className}
      activeProps={{ className: activeClassName }}
    >
      {label}
    </Link>
  );
}

export function StageNav() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const links = useNavLinks();
  // Controlled so tapping a link closes the sheet — client-side navigation alone
  // leaves a Radix Sheet open (Leon, #ui-polish: the drawer stayed open on preview
  // when you tapped "Wgraj zdjęcie").
  const [menuOpen, setMenuOpen] = useState(false);

  const linkClass =
    "rounded-full px-3 py-1.5 text-sm font-medium text-foreground/65 transition-colors hover:bg-foreground/[0.07] hover:text-foreground";
  const activeClass = "bg-primary/12 text-primary hover:bg-primary/12 hover:text-primary";
  // Mobile sheet link classes, shared by the static links and the resume-aware
  // upload link so both branches stay visually identical.
  const mobileLinkClass =
    "rounded-full px-4 py-3 text-center text-base font-medium text-foreground/80 transition-colors hover:bg-foreground/[0.06]";
  const mobileActiveClass = "bg-primary/12 text-primary";

  if (isMobile) {
    return (
      <div className="pointer-events-none fixed inset-x-4 top-[max(1rem,env(safe-area-inset-top))] z-50 flex items-center justify-between gap-3">
        <Link
          to="/"
          aria-label={t("app.name")}
          className={cn(
            CHROME_SURFACE,
            // min-h-11 matches the 44px menu / controls / info buttons (Leon, #ui-polish).
            "pointer-events-auto flex min-h-11 items-center rounded-full px-3.5 text-foreground transition-transform active:scale-[0.98]",
          )}
        >
          <Logo className="h-7" />
        </Link>
        <div className="pointer-events-auto flex items-center gap-2">
          <CartNav
            triggerClassName={cn(
              CHROME_SURFACE,
              "size-11 rounded-full text-foreground transition-transform active:scale-[0.97]",
            )}
          />
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("nav.menu")}
                data-testid="stage-nav-trigger"
                className={cn(
                  CHROME_SURFACE,
                  "size-11 rounded-full text-foreground transition-transform active:scale-[0.97]",
                )}
              >
                <Menu aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="stage-nav-sheet"
              // Don't pull focus to the close button on open — the default auto-focus
              // ring looked odd on tap (Leon, #ui-polish). Keyboard users still Tab in.
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="flex w-72 flex-col border-chrome-line/25 bg-chrome pt-[max(1.5rem,env(safe-area-inset-top))] [&>button:first-of-type]:hidden"
            >
              {/* Own close button in the header row so it lines up with the logo (the
                built-in absolute X sits higher); the built-in one is hidden above. */}
              <SheetHeader className="flex-row items-center justify-between space-y-0">
                <SheetTitle className="flex items-center font-display tracking-tight">
                  <Logo className="h-7 text-foreground" />
                </SheetTitle>
                <SheetClose className="inline-flex size-9 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50">
                  <X aria-hidden="true" className="size-5" />
                  <span className="sr-only">{t("nav.close")}</span>
                </SheetClose>
              </SheetHeader>
              {/* Links vertically centred so they sit in easy thumb reach, not up top. */}
              <nav className="flex flex-1 flex-col justify-center gap-1">
                {links.map((l) =>
                  // "Wgraj zdjęcie" is resume-aware (matches the hero CTA) — see UploadNavLink.
                  l.to === "/upload" ? (
                    <UploadNavLink
                      key={l.to}
                      label={l.label}
                      className={mobileLinkClass}
                      activeClassName={mobileActiveClass}
                      onClick={() => setMenuOpen(false)}
                    />
                  ) : (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMenuOpen(false)}
                      activeOptions={{ exact: l.active === "exact" }}
                      className={mobileLinkClass}
                      activeProps={{ className: mobileActiveClass }}
                    >
                      {l.label}
                    </Link>
                  ),
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    );
  }

  return (
    // One connected floating bar, left-anchored (centre stays clear for the cushion).
    <div className="pointer-events-none fixed inset-x-4 top-4 z-50">
      <div
        className={cn(
          CHROME_SURFACE,
          "pointer-events-auto inline-flex items-center gap-1 rounded-full py-1.5 pr-2 pl-2.5",
        )}
      >
        <Link
          to="/"
          aria-label={t("app.name")}
          className="flex items-center rounded-full px-1.5 py-0.5 text-foreground transition-opacity hover:opacity-80"
        >
          <Logo className="h-7" />
        </Link>
        <span
          aria-hidden="true"
          className={cn(stitchClass("vertical"), "mx-1.5 h-5 self-center")}
        />
        <nav className="flex items-center gap-0.5">
          {links.map((l) =>
            // "Wgraj zdjęcie" is resume-aware (matches the hero CTA) — see UploadNavLink.
            l.to === "/upload" ? (
              <UploadNavLink
                key={l.to}
                label={l.label}
                className={linkClass}
                activeClassName={cn(linkClass, activeClass)}
              />
            ) : (
              <Link
                key={l.to}
                to={l.to}
                activeOptions={{ exact: l.active === "exact" }}
                className={linkClass}
                activeProps={{ className: cn(linkClass, activeClass) }}
              >
                {l.label}
              </Link>
            ),
          )}
        </nav>
        <span
          aria-hidden="true"
          className={cn(stitchClass("vertical"), "mx-1.5 h-5 self-center")}
        />
        <CartNav
          triggerClassName={cn(
            "size-9 rounded-full text-foreground/65 transition-colors hover:bg-foreground/[0.07] hover:text-foreground",
          )}
        />
      </div>
    </div>
  );
}
